import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { deleteFile } from "../api/files.api";
import { CLIENT_UPLOAD_CONCURRENCY_MAX } from "../constants/limits";
import type { StorageApiErrorBody } from "../types/project-storage.types";
import { runConcurrent } from "../utils/runConcurrent";
import { folderChildrenQueryKey } from "./useFolderChildren";
import { folderTreeQueryKey } from "./useFolderTree";

export type DeleteFilesResult = {
  deletedCount: number;
  failedCount: number;
};

export function useDeleteFiles(mmCode: number | null, folderId: string | null) {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const invalidateFolderData = useCallback(async () => {
    if (mmCode === null || !folderId) return;

    await Promise.all([
      queryClient.invalidateQueries(folderChildrenQueryKey(mmCode, folderId)),
      queryClient.invalidateQueries(folderTreeQueryKey(mmCode)),
    ]);
  }, [folderId, mmCode, queryClient]);

  const deleteFiles = useCallback(
    async (fileIds: string[]): Promise<DeleteFilesResult> => {
      if (mmCode === null || fileIds.length === 0) {
        return { deletedCount: 0, failedCount: 0 };
      }

      setIsDeleting(true);

      let deletedCount = 0;
      let failedCount = 0;

      try {
        await runConcurrent(
          fileIds,
          CLIENT_UPLOAD_CONCURRENCY_MAX,
          async (fileId) => {
            try {
              await deleteFile(mmCode, fileId);
              deletedCount += 1;
            } catch (error) {
              const body = (error as any)?.response?.data as
                | StorageApiErrorBody
                | undefined;

              if (body?.errorCode === "FILE_NOT_FOUND") {
                deletedCount += 1;
                return;
              }

              failedCount += 1;
            }
          }
        );

        if (deletedCount > 0) {
          await invalidateFolderData();

          toast.success(
            deletedCount === 1
              ? "Arquivo excluído com sucesso."
              : `${deletedCount} arquivos excluídos com sucesso.`
          );
        }

        if (failedCount > 0) {
          toast.error("Não foi possível excluir alguns arquivos.");
        }

        return { deletedCount, failedCount };
      } finally {
        setIsDeleting(false);
      }
    },
    [invalidateFolderData, mmCode]
  );

  return {
    deleteFiles,
    isDeleting,
  };
}
